import * as React from 'react';

import Mpegts from 'mpegts.js';
import Hls from 'hls.js';
import { View, StyleSheet, Pressable, StyleProp, ViewStyle } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import Badge from './Badge';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

function ViewOrPressable(props: {
    onClickEvent?: () => Promise<void>;
    style: StyleProp<ViewStyle>;
    children: React.ReactNode | undefined;
}) {

    if (props.onClickEvent) {
        return <Pressable
            onPress={props.onClickEvent}
            style={props.style}
        >
            {props.children}
        </Pressable>;
    }

    return <View style={props.style}>
        {props.children}
    </View>;
}

export interface VideoPlayerProps {
    watermark?: string;
    onClickEvent?: (isPlaying: boolean) => void;
    camera: Camera;
    enableZoom: boolean;
    enableCameraInfo: boolean;
}

type VideoPlayerState = {
    flvPlayer?: Mpegts.Player;
    hlsPlayer?: Hls;
    videoRef: React.RefObject<HTMLVideoElement>;
    containerRef: React.RefObject<HTMLDivElement>;
    playing: boolean;
    loading: boolean;
    myProps: VideoPlayerProps;
    playbackRate: number;
    allowedPlaybackRates: number[];
};

interface Statistics {
    speed: number;
    decodedFrames: number;
    droppedFrames: number;
    currentSegmentIndex: number;
    hasRedirect: boolean;
    loaderType: string;
    playerType: string;
    totalSegmentCount: number;
    url: string;
}

export default class VideoPlayer extends React.Component<VideoPlayerProps> {

    public state: VideoPlayerState;
    private displayCameraInfo: boolean;
    private src: string = '';
    private customHeaders?: { [key: string]: string; };
    private isLive = true;
    private onNeedFreshSrc?: () => Promise<{ src: string; headers?: { [key: string]: string; }; }>;
    private reconnectTimeout: NodeJS.Timeout | null = null;
    private loadingTimeout: NodeJS.Timeout | null = null;

    constructor(props: VideoPlayerProps) {
        super(props);
        this.state = {
            flvPlayer: undefined,
            hlsPlayer: undefined,
            videoRef: React.createRef<HTMLVideoElement>(),
            containerRef: React.createRef<HTMLDivElement>(),
            playing: true,
            loading: false,
            myProps: props,
            playbackRate: 1.0,
            allowedPlaybackRates: [0.25, 0.5, 1.0, 2.0, 4.0, 8.0, 16.0],
        };
        this.displayCameraInfo = true;
    }

    public setPlaybackRate(playbackRate: number) {
        this.setState({ playbackRate });

        const video = this.state.videoRef?.current;
        if (!video) {
            return console.error('[setPlaybackRate] video not ready');
        }

        video.playbackRate = playbackRate;
    }

    public showLoading() {
        this.destroyPlayers();
        this.setState({ loading: true });
    }

    public destroyPlayers() {
        if (this.state.flvPlayer) {
            this.state.flvPlayer.unload();
            this.setState({ flvPlayer: undefined });
        }
        if (this.state.hlsPlayer) {
            this.state.hlsPlayer.destroy();
            this.setState({ hlsPlayer: undefined });
        }
    }

    componentWillUnmount() {
        this.destroyPlayers();
    }

    /**
     * Set the current video playing at given time
     * @param timeAt in seconds
     * @returns
     */
    setTimeAt(timeAt: number) {
        const video = this.state.videoRef?.current;

        if (!video) {
            return console.error('[setTimeAt] video not ready');
        }

        if (video.readyState >= 1) {
            video.currentTime = timeAt;
            return;
        }

        video.addEventListener('loadedmetadata', () => {
            video.currentTime = timeAt;
        }, false);
    }

    /**
     *
     * @returns currentTime in seconds
     */
    getTime() {
        const video = this.state.videoRef?.current;
        if (!video) {
            throw new Error('[getTime] video not ready');
        }

        return video.currentTime;
    }

    recreateVideoElement(callback?: () => void) {

        const div = this.state.containerRef.current;
        if (div) {
            div.innerHTML = '';

            const video = document.createElement('video');
            video.autoplay = true;
            video.controls = false;
            video.disablePictureInPicture = true;
            video.muted = true;
            video.style.width = '100%';
            video.style.height = '100%';
            video.style.objectFit = 'fill';
            video.playbackRate = this.state.playbackRate;

            if (callback) {
                video.addEventListener('ended', callback);
            }

            div.appendChild(video);
            this.setState({ videoRef: { current: video } });

            return video;
        }

        return null;
    }

    setSrc(src: string, customHeaders?: { [key: string]: string; }, onVideoEnded?: () => void, isLive = true, onNeedFreshSrc?: () => Promise<{ src: string; headers?: { [key: string]: string; }; }>) {
        this.src = src;
        this.customHeaders = customHeaders;
        this.isLive = isLive;
        this.onNeedFreshSrc = onNeedFreshSrc;
        let video = this.state.videoRef?.current;

        video = this.recreateVideoElement(onVideoEnded);

        if (!video) {
            return console.error('[setSrc] video not ready');
        }

        const srcUrl = new URL(src);
        const newSrc = srcUrl.origin + srcUrl.pathname;
        let isMP4Video = false;
        try {
            isMP4Video = newSrc.endsWith('.mp4');
        } catch (error) {
            console.error('invalid URL for video source:', src);
        }

        let currentVideoSrc = '';
        try {
            const videoUrl = new URL(video.src);
            currentVideoSrc = videoUrl.origin + videoUrl.pathname;
        } catch (err) {
            // ...
        }
        if (newSrc === currentVideoSrc) {
            return;
        }

        this.destroyPlayers();
        if (isMP4Video) {
            video.src = src;
            video.playbackRate = this.state.playbackRate;
            if (!this.state.playing) {
                video.pause();
            }

            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
                this.reconnectTimeout = null;
            }
            if (this.loadingTimeout) {
                clearTimeout(this.loadingTimeout);
                this.loadingTimeout = null;
            }
            this.setState({ loading: false });
            return;
        }

        this.setPlaybackRate(1.0);

        if (this.state.myProps.camera.thirdPartyCamera?.type === 'hls') {
            if (Hls.isSupported()) {
                const hlsPlayer = new Hls({
                    xhrSetup: xhr => {
                        if (this.state.myProps.camera.thirdPartyToken && this.state.myProps.camera.thirdPartyCamera) {
                            xhr.setRequestHeader('Authorization', `Bearer ${this.state.myProps.camera.thirdPartyToken}`);
                        }
                    }
                });
                hlsPlayer.loadSource(src);
                hlsPlayer.attachMedia(video);
                video.play();

                this.setState({ hlsPlayer });
                return;
            } else {
                return console.error('unsupported browser.');
            }
        } else {
            const flvPlayer = Mpegts.createPlayer({
                type: 'mse',
                url: src,
                isLive: this.isLive,
                withCredentials: false,
            }, {
                enableWorker: true,
                lazyLoadMaxDuration: 3 * 60,
                seekType: 'range',
                liveBufferLatencyChasing: true,
                headers: this.state.myProps.camera.thirdPartyCamera && !this.state.myProps.camera.originalCameraId ?
                    this.state.myProps.camera.thirdPartyToken ? { Authorization: `Bearer ${this.state.myProps.camera.thirdPartyToken}` } : undefined
                    : customHeaders
            });
            flvPlayer.on(Mpegts.Events.ERROR,
                (errorType: string, errorDetail: string) => {
                    switch (errorType) {
                        case Mpegts.ErrorTypes.NETWORK_ERROR:
                            this.attemptReconnectFlv();
                            break;
                        case Mpegts.ErrorTypes.MEDIA_ERROR:
                            console.error(errorType, errorDetail);
                            this.attemptReconnectFlv();
                            break;
                        default:
                            console.error(errorType, errorDetail);
                    }
                }
            );

            flvPlayer.on(Mpegts.Events.STATISTICS_INFO, (stats: Statistics) => {
                if (stats.speed > 0) {
                    if (this.loadingTimeout) {
                        clearTimeout(this.loadingTimeout);
                        this.loadingTimeout = null;
                    }
                    this.setState({ loading: false });
                } else {
                    if (!this.loadingTimeout && !this.state.loading) {
                        this.loadingTimeout = setTimeout(() => {
                            this.setState({ loading: true });
                        }, 5000);
                    }
                }
            });

            flvPlayer.attachMediaElement(video);
            flvPlayer.load();
            flvPlayer.play();
            this.setState({ flvPlayer });
        }
    }

    async initPlayer() {
        let src = this.src;
        let customHeaders = this.customHeaders;

        if (this.onNeedFreshSrc) {
            try {
                const fresh = await this.onNeedFreshSrc();
                src = fresh.src;
                customHeaders = fresh.headers;
            } catch (err) {
                console.error(err);
                this.attemptReconnectFlv();
                return;
            }
        }

        this.setSrc(src, customHeaders, undefined, this.isLive, this.onNeedFreshSrc);
    }

    private attemptReconnectFlv(): void {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        this.reconnectTimeout = setTimeout(() => {
            this.setState({ loading: true });
            this.destroyPlayers();
            this.initPlayer();
        }, 2000);
    }

    private async syncVideoPlay() {
        const video = this.state.videoRef?.current;

        if (!video) {
            return console.error('[syncVideoPlay] video not ready');
        }

        if (!this.state.playing) {
            video.pause();
        } else {
            await video.play();
        }
    }

    requestFullscreen() {
        const video = this.state.videoRef?.current;

        if (!video) {
            return console.error('[requestFullscreen] video not ready');
        }

        if (video.requestFullscreen) {
            video.requestFullscreen();
        }
    }

    setPlaying(playing: boolean) {
        this.setState({ playing });
    }

    componentDidUpdate(prevProps: VideoPlayerProps, prevStates: VideoPlayerState) {

        // this works like `useEffect(()=>[playing])`
        if (prevStates.playing !== this.state.playing) {
            this.syncVideoPlay();
        }

        if (prevStates.flvPlayer && prevStates.flvPlayer !== this.state.flvPlayer) {
            prevStates.flvPlayer.unload();
        }
    }

    render() {
        return (
            <View style={{ display: 'flex', flex: 1 }}>
                <View style={{ backgroundColor: 'black', display: 'flex', flex: 1 }}>
                    <ViewOrPressable
                        onClickEvent={this.state.myProps?.onClickEvent ? async () => {
                            if (this.state.myProps?.onClickEvent) {
                                return this.state.myProps.onClickEvent(this.state.playing);
                            }
                        } : undefined}
                        style={{ position: 'absolute', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', zIndex: this.state.loading ? 10 : 1, backgroundColor: 'black' }}>
                        <View style={{ width: 50, height: 50 }}>
                            <FontAwesomeIcon size='2x' spin={true} icon={faSpinner} color='#fff' />
                        </View>
                    </ViewOrPressable>
                    <ViewOrPressable
                        onClickEvent={this.state.myProps?.onClickEvent ? async () => {
                            if (this.state.myProps?.onClickEvent) {
                                return this.state.myProps.onClickEvent(this.state.playing);
                            }
                        } : undefined}
                        style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 4, flexDirection: 'column', justifyContent: 'space-between' }}
                    >
                        {this.state.myProps.enableZoom ?
                            <TransformWrapper
                                initialScale={1}
                                minScale={1}
                                maxScale={5}
                                doubleClick={{ disabled: true }}
                                wheel={{ step: 0.1 }}
                            >
                                <TransformComponent
                                    wrapperStyle={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'absolute',
                                        zIndex: 2
                                    }}
                                    contentStyle={{
                                        width: '100%',
                                        height: '100%'
                                    }}
                                >
                                    <div ref={this.state.containerRef} style={{ zIndex: 2, width: '100%', height: '100%', position: 'absolute', left: 0, top: 0 }} />
                                </TransformComponent>
                            </TransformWrapper>
                            : <div ref={this.state.containerRef} style={{ zIndex: 2, width: '100%', height: '100%', position: 'absolute', left: 0, top: 0 }} />
                        }
                        {(this.state.myProps.enableCameraInfo && this.displayCameraInfo) ?
                            <Pressable
                                style={styles.badgeTop}
                                onPress={() => this.displayCameraInfo = !this.displayCameraInfo}
                            >
                                <View pointerEvents='auto'>
                                    <Badge
                                        color='transparencyBlack'
                                        mode='SQUARE'
                                        textStyle={styles.badgeText}
                                    >
                                        {this.state.myProps.camera.address}
                                    </Badge>
                                </View>
                            </Pressable>
                            : null}
                        <Badge
                            color='transparencyBlack'
                            mode='SQUARE'
                            style={styles.badgeTopRight}
                            textStyle={styles.badgeText}
                        >
                            {this.state.myProps.watermark}
                        </Badge>
                        {(this.state.myProps.enableCameraInfo && this.displayCameraInfo) ?
                            <Pressable
                                style={styles.badgeBottom}
                                onPress={() => this.displayCameraInfo = !this.displayCameraInfo}
                            >
                                <View pointerEvents='auto'>
                                    <Badge
                                        color='transparencyBlack'
                                        mode='SQUARE'
                                        textStyle={styles.badgeText}
                                    >
                                        {this.state.myProps.camera.title}
                                    </Badge>
                                </View>
                            </Pressable>
                            : null
                        }
                    </ViewOrPressable>
                </View>
            </View >
        );
    }
}

const styles = StyleSheet.create({
    badgeTop: {
        position: 'absolute',
        top: 24,
        left: 8,
        zIndex: 3
    },
    badgeTopRight: {
        position: 'absolute',
        top: 24,
        right: 8,
        zIndex: 3
    },
    badgeBottom: {
        position: 'absolute',
        bottom: 8,
        left: 8,
        zIndex: 3
    },
    badgeText: {
        fontWeight: '700',
        fontSize: 14
    }
});
